import React, { useState, useEffect, useRef } from "react";
import { 
    Box, 
    Typography, 
    Card, 
    CardContent, 
    Alert, 
    AppBar, 
    Toolbar, 
    IconButton, 
    Menu, 
    MenuItem,
    Skeleton
} from "@mui/material";
import FMBlogo from "../../assests/FMBlogo.png";
import Logo from "../../assests/logo.png";
import MenuIcon from "@mui/icons-material/Menu";
import { useLocation, useNavigate } from "react-router-dom";
import Cookies from "js-cookie";
import {
    createPusherInstance,
    subscribeToQueueUpdates,
} from "../../config/pusher";
import { checkQueueStatus, getMenus } from "../../api/api";
import { getUserFrontendUrl, getFrontendUrl} from "../../config/config";
import ScrollButtons from "../../components/ScrollButtons";
import Carousel from "../Admin/DisplayOption/Carousel";

const GetQueue = () => {
    const [error, setError] = useState(null);
    const [isAssisted, setIsAssisted] = useState(false);
    const [isBeingServed, setIsBeingServed] = useState(false);
    const [showServingMessage, setShowServingMessage] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [servicesFromApi, setServicesFromApi] = useState([]);
    const [menuItems, setMenuItems] = useState([]);
    const [logoLoaded, setLogoLoaded] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    
    // Check sessionStorage for redirect state (from getticket when service selection is disabled)
    const redirectStateFromStorage = (() => {
        try {
            const stored = sessionStorage.getItem('getticket_redirect_state');
            if (stored) {
                const parsed = JSON.parse(stored);
                sessionStorage.removeItem('getticket_redirect_state'); // Clean up after reading
                return parsed;
            }
        } catch (error) {
            console.error('Error reading redirect state from sessionStorage:', error);
        }
        return null;
    })();
    
    // Use redirect state from sessionStorage if available, otherwise use location.state
    const stateFromLocation = location.state || {};
    const redirectState = redirectStateFromStorage || stateFromLocation;
    const { selectedOptions, queueNumber, serviceSelectionDisabled: stateServiceSelectionDisabled, ticketData: stateTicketData } = redirectState;
    const [serviceSelectionDisabled, setServiceSelectionDisabled] = useState(stateServiceSelectionDisabled || false);
    
    // Track if service selection was enabled when queue was created
    // This helps determine if we should show default services
    const wasServiceSelectionEnabledAtCreation = stateServiceSelectionDisabled === false || 
        (stateServiceSelectionDisabled === undefined && selectedOptions !== undefined);
    const open = Boolean(anchorEl);
    
    // If service selection is disabled, always use empty array
    // Use services from API if available, otherwise use selectedOptions from location.state
    // But if serviceSelectionDisabled is true, force empty array
    // IMPORTANT: Even if selectedOptions has services, ignore them if service selection is disabled
    // Always return empty array if service selection is disabled, regardless of what's in selectedOptions or servicesFromApi
    const displayServices = serviceSelectionDisabled 
        ? [] 
        : (servicesFromApi.length > 0 
            ? servicesFromApi 
            : (Array.isArray(selectedOptions) && selectedOptions.length > 0 
                ? selectedOptions 
                : []));
    // Only show services section if service selection is enabled AND there are services to display
    const hasSelectedOptions = !serviceSelectionDisabled && displayServices.length > 0;

    // Debug logging to understand the data structure
    console.log("GetQueue - selectedOptions:", selectedOptions);
    console.log("GetQueue - queueNumber:", queueNumber);
    console.log("GetQueue - serviceSelectionDisabled:", serviceSelectionDisabled);
    console.log("GetQueue - servicesFromApi:", servicesFromApi);
    console.log("GetQueue - displayServices:", displayServices);

    // Use ref to store current queue number for pusher events
    const currentQueueNumberRef = useRef(null);
    // Track if we've initialized service selection status from API
    const serviceSelectionInitializedRef = useRef(false);

    // Get ticket details from redirect state (sessionStorage), location state, or cookie
    const ticketDataFromRedirect = redirectStateFromStorage?.ticketData;
    const ticketDataFromState = stateTicketData || location.state?.ticketData;
    const ticketDetails = Cookies.get("ticketDetails");
    let ticketData = ticketDataFromRedirect || ticketDataFromState || null;
    
    // If not in state, try cookie
    if (!ticketData && ticketDetails) {
        try {
            ticketData = JSON.parse(ticketDetails);
            
            // Validate cookie: if queue number from state doesn't match cookie, clear it
            // This prevents using cookies from different queues/clients
            if (queueNumber && ticketData.queue_number && ticketData.queue_number !== queueNumber) {
                console.log('Queue number mismatch detected, clearing cookie');
                Cookies.remove("ticketDetails", { path: "/" });
                ticketData = null;
            }
            
            // If queue status is ASSISTED or LOST, clear the cookie
            if (ticketData.queue_status === 'ASSISTED' || ticketData.queue_status === 'LOST') {
                console.log('Queue is inactive (ASSISTED/LOST), clearing cookie');
                Cookies.remove("ticketDetails", { path: "/" });
                ticketData = null;
            }
        } catch (error) {
            console.error("Error parsing ticket details from cookie:", error);
            // Clear invalid cookie
            Cookies.remove("ticketDetails", { path: "/" });
            ticketData = null;
        }
    }
    
    const currentQueueNumber = queueNumber || ticketData?.queue_number;
    const branchId = ticketData?.client?.id; // Extract branch ID from ticket data
    
    // Initialize service selection status from ticketData (cookie/state) only once
    // Don't update if we've already initialized from API
    useEffect(() => {
        if (!serviceSelectionInitializedRef.current && ticketData?.client?.service_selection_enabled !== undefined) {
            const isDisabled = ticketData.client.service_selection_enabled === false;
            console.log('🚫 Initial service selection status from ticketData:', isDisabled ? 'disabled' : 'enabled');
            setServiceSelectionDisabled(isDisabled);
            if (isDisabled) {
                setServicesFromApi([]);
            }
        }
    }, [ticketData]);
    
    // Clear services whenever serviceSelectionDisabled changes to true
    // Only clear if it's actually disabled (not just initializing)
    useEffect(() => {
        if (serviceSelectionDisabled && serviceSelectionInitializedRef.current) {
            console.log('🚫 Service selection disabled - clearing all services');
            setServicesFromApi([]);
        }
    }, [serviceSelectionDisabled]);

    // Scroll to top when serving message appears
    useEffect(() => {
        if (showServingMessage) {
            // Scroll immediately with smooth behavior
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            
            // Also scroll after a small delay to ensure it works
            const scrollTimeout = setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                document.documentElement.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            }, 100);
            
            return () => clearTimeout(scrollTimeout);
        }
    }, [showServingMessage]);

    // Scroll to top when serving message appears
    useEffect(() => {
        if (showServingMessage) {
            // Scroll immediately
            window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
            document.documentElement.scrollTop = 0;
            document.body.scrollTop = 0;
            
            // Also scroll after a small delay to ensure it works
            const scrollTimeout = setTimeout(() => {
                window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                document.documentElement.scrollTop = 0;
                document.body.scrollTop = 0;
            }, 100);
            
            return () => clearTimeout(scrollTimeout);
        }
    }, [showServingMessage]);
    
    // Get logo filename from database and construct the URL
    const logoFilename = ticketData?.client?.logo;
    const logoUrl = logoFilename 
        ? getFrontendUrl(`/storage/logos/${logoFilename}`)
        : FMBlogo; // Fallback to default logo if not available

    // Preload logo for faster loading (only for remote URLs, not local imports)
    useEffect(() => {
        if (logoUrl && logoFilename && logoUrl.startsWith('http')) {
            try {
                // Create link element for preloading
                const link = document.createElement('link');
                link.rel = 'preload';
                link.as = 'image';
                link.href = logoUrl;
                link.fetchPriority = 'high';
                document.head.appendChild(link);

                // Also preconnect to the domain for faster connection
                const preconnect = document.createElement('link');
                preconnect.rel = 'preconnect';
                preconnect.href = new URL(logoUrl).origin;
                document.head.appendChild(preconnect);

                return () => {
                    // Cleanup on unmount
                    if (document.head.contains(link)) {
                        document.head.removeChild(link);
                    }
                    if (document.head.contains(preconnect)) {
                        document.head.removeChild(preconnect);
                    }
                };
            } catch (error) {
                console.warn('Error preloading logo:', error);
            }
        }
    }, [logoUrl, logoFilename]);

    // Reset logo loaded state when logoUrl changes and add timeout fallback
    useEffect(() => {
        setLogoLoaded(false);
        
        // Fallback: if logo doesn't load within 5 seconds, show it anyway
        const timeout = setTimeout(() => {
            console.warn('Logo loading timeout, showing image anyway');
            setLogoLoaded(true);
        }, 5000);
        
        return () => clearTimeout(timeout);
    }, [logoUrl]);

    // Update ref whenever currentQueueNumber changes
    currentQueueNumberRef.current = currentQueueNumber;

    // Fetch menu items when branchId is available
    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!branchId) {
                return;
            }

            try {
                const response = await getMenus(branchId);
                if (response.success) {
                    setMenuItems(response.data || []);
                }
            } catch (error) {
                console.error("Error fetching menu items:", error);
                // Set empty array on error to prevent issues
                setMenuItems([]);
            }
        };

        fetchMenuItems();
    }, [branchId]);

    // Check queue status on component load
    useEffect(() => {
        const checkCurrentQueueStatus = async () => {
            if (!currentQueueNumber || !branchId) {
                return;
            }

            try {
                const response = await checkQueueStatus(
                    currentQueueNumber,
                    branchId
                );

                if (response.is_assisted) {
                    // Clear the cookie since the queue has been assisted
                    Cookies.remove("ticketDetails", { path: "/" });
                    console.log("🎟 Queue assisted, clearing cookie");
                    
                    // Scroll to top before navigation
                    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                    
                    navigate("/home/thank-you", {
                        state: {
                            queueNumber: currentQueueNumber,
                            selectedOptions,
                            ticketCode: ticketData?.ticket_code || "DEMO",
                            isDemoMode: !ticketData,
                            autoRedirect: true,
                            reason: "Queue was already assisted",
                            isLost: false,
                            ticketData: ticketData // Pass ticketData with logo info
                        },
                    });
                } else if (response.queue?.QueueStatus === "LOST" || response.status === "LOST") {
                    // Clear the cookie since the queue has been lost
                    Cookies.remove("ticketDetails", { path: "/" });
                    console.log("🎟 Queue lost, clearing cookie");
                    
                    // Scroll to top before navigation
                    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                    
                    navigate("/home/thank-you", {
                        state: {
                            queueNumber: currentQueueNumber,
                            selectedOptions,
                            ticketCode: ticketData?.ticket_code || "DEMO",
                            isDemoMode: !ticketData,
                            autoRedirect: true,
                            reason: "Queue was marked as lost",
                            isLost: true,
                            ticketData: ticketData // Pass ticketData with logo info
                        },
                    });
                } else if (response.is_serving) {
                    setIsBeingServed(true);
                    setShowServingMessage(true);
                    // Scroll to top when serving message appears
                    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                    document.documentElement.scrollTop = 0;
                    document.body.scrollTop = 0;
                }
                
                // Check service_selection_enabled from API response if available
                // API response is the source of truth - always update if API provides it (even after initialization)
                // This handles cases where admin changes service selection status
                const isServiceSelectionDisabled = response.service_selection_enabled === false;
                const wasServiceSelectionDisabled = serviceSelectionDisabled;
                
                if (response.service_selection_enabled !== undefined) {
                    if (!serviceSelectionInitializedRef.current) {
                        // First time initialization
                        console.log('✅ Service selection status from API (initial):', isServiceSelectionDisabled ? 'disabled' : 'enabled');
                        setServiceSelectionDisabled(isServiceSelectionDisabled);
                        serviceSelectionInitializedRef.current = true;
                    } else if (wasServiceSelectionDisabled !== isServiceSelectionDisabled) {
                        // Status changed after initialization (admin changed setting)
                        console.log('🔄 Service selection status changed from API:', wasServiceSelectionDisabled ? 'disabled' : 'enabled', '→', isServiceSelectionDisabled ? 'disabled' : 'enabled');
                        setServiceSelectionDisabled(isServiceSelectionDisabled);
                    }
                }
                
                // Update services from API response if available
                // IMPORTANT: Always respect current service selection status
                if (isServiceSelectionDisabled) {
                    // Service selection is disabled - clear all services
                    console.log('🚫 Service selection disabled - clearing services from API response');
                    setServicesFromApi([]);
                } else {
                    // Service selection is enabled - use services from API if available
                    if (response.services && Array.isArray(response.services) && response.services.length > 0) {
                        console.log('✅ Service selection enabled - setting services from API:', response.services);
                        setServicesFromApi(response.services);
                    } else {
                        // No services in API response - clear services
                        // This handles case where service selection was disabled when queue was created
                        // and now it's enabled but no services were ever selected
                        console.log('ℹ️ Service selection enabled but no services in API response');
                        setServicesFromApi([]);
                    }
                }
            } catch (error) {
                console.error("Error checking queue status:", error);
            }
        };

        // Only check queue status if we have a valid queue number
        if (currentQueueNumber && branchId) {
            checkCurrentQueueStatus();
        }
        // Remove serviceSelectionDisabled from dependencies to prevent re-running when it changes
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentQueueNumber, branchId, navigate, queueNumber, selectedOptions, ticketData]);

    // Fix scrolling issue by overriding global overflow hidden
    useEffect(() => {
        // Override global overflow hidden for this page
        const rootElement = document.getElementById('root');
        const htmlElement = document.documentElement;
        
        // Enable scrolling on body and html
        document.body.style.overflow = 'auto';
        document.body.style.height = 'auto';
        htmlElement.style.overflow = 'auto';
        htmlElement.style.height = 'auto';
        
        if (rootElement) {
            rootElement.style.overflow = 'auto';
            rootElement.style.height = 'auto';
        }
        
        // Add a CSS class to ensure scrolling works
        document.body.classList.add('scrollable-page');
        htmlElement.classList.add('scrollable-page');
        
        return () => {
            // Restore original overflow settings when component unmounts
            document.body.style.overflow = 'hidden';
            document.body.style.height = '100%';
            htmlElement.style.overflow = 'hidden';
            htmlElement.style.height = '100%';
            if (rootElement) {
                rootElement.style.overflow = 'hidden';
                rootElement.style.height = '100%';
            }
            document.body.classList.remove('scrollable-page');
            htmlElement.classList.remove('scrollable-page');
        };
    }, []);


    useEffect(() => {
        if (!currentQueueNumber) {
            setError("No queue number found");
            return;
        }

        // Initialize Pusher for real-time updates
        const pusher = createPusherInstance();

        pusher.connection.bind("connected", () => {
            console.log("✅ GetQueue: Pusher connected successfully");
        });

        pusher.connection.bind("error", (error) => {
            console.error("❌ GetQueue: Pusher connection error:", error);
        });

        const channel = subscribeToQueueUpdates(pusher, (data) => {
            const currentNumber = currentQueueNumberRef.current;

            // Get queue data from either data.queue or data.data.queue (Laravel Echo formats can vary)
            const queueData = data.queue || data.data?.queue;
            const actionData = data.action || data.data?.action;

            if (queueData) {
                // Check if this update is for the current user's queue
                // Use loose equality to handle string/number conversion
                if (queueData.QueueNr == currentNumber) {
                    if (
                        actionData === "assisted" ||
                        queueData.QueueStatus === "ASSISTED"
                    ) {
                        setIsAssisted(true);
                        
                        // Clear the cookie since the queue has been assisted
                        Cookies.remove("ticketDetails", { path: "/" });
                        console.log("🎟 Queue assisted via Pusher, clearing cookie");
                        
                        // Scroll to top before navigation
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        
                        // Redirect immediately to thank you page
                        navigate("/home/thank-you", {
                            state: {
                                queueNumber: currentQueueNumber,
                                selectedOptions,
                                ticketCode: ticketData?.ticket_code || "DEMO",
                                isDemoMode: !ticketData,
                                isLost: false,
                                ticketData: ticketData // Pass ticketData with logo info
                            }
                        });
                    } else if (
                        actionData === "lost" ||
                        queueData.QueueStatus === "LOST"
                    ) {
                        setIsAssisted(true);
                        
                        // Clear the cookie since the queue has been lost
                        Cookies.remove("ticketDetails", { path: "/" });
                        console.log("🎟 Queue lost via Pusher, clearing cookie");
                        
                        // Scroll to top before navigation
                        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
                        
                        // Redirect immediately to thank you page with lost flag
                        navigate("/home/thank-you", {
                            state: {
                                queueNumber: currentQueueNumber,
                                selectedOptions,
                                ticketCode: ticketData?.ticket_code || "DEMO",
                                isDemoMode: !ticketData,
                                isLost: true,
                                ticketData: ticketData // Pass ticketData with logo info
                            }
                        });
                    } else if (
                        actionData === "serving" ||
                        queueData.QueueStatus === "SERVING"
                    ) {
                        setIsBeingServed(true);
                        setShowServingMessage(true);
                        // Scroll to top when serving message appears
                        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
                        document.documentElement.scrollTop = 0;
                        document.body.scrollTop = 0;
                    }
                }
            }
        });

        return () => {
            channel.unbind("queue.updated");
            pusher.unsubscribe("queues");
            pusher.disconnect();
        };
    }, [currentQueueNumber, navigate, selectedOptions, ticketData]);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    return (
        <>
            <Box sx={{ flexGrow: 1 }}>
                <AppBar position="static">
                    <Toolbar>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mr: 2,
                            }}
                        >
                            <img
                                src={Logo}
                                alt="Logo"
                                style={{
                                    height: "24px",
                                    marginRight: "8px",
                                    filter: "brightness(0) invert(1)", // Make logo white
                                }}
                            />
                        </Box>
                        <Typography
                            variant="h6"
                            component="div"
                            sx={{ flexGrow: 1 }}
                        >
                        </Typography>

                        <IconButton
                            size="large"
                            color="inherit"
                            aria-label="menu"
                            sx={{ ml: 2 }}
                            onClick={handleClick}
                        >
                            <MenuIcon />
                        </IconButton>

                        {/* Top menu */}
                        <Menu
                            anchorEl={anchorEl}
                            open={open}
                            onClose={handleClose}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                            transformOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            {/* Default HOME menu item */}
                            <MenuItem onClick={handleClose}>
                                <a
                                    href={getUserFrontendUrl(`/get-ticket/${ticketData?.ticket_code || "DEMO"}`)}
                                    rel="noopener noreferrer"
                                    style={{
                                        textDecoration: "none",
                                        color: "inherit",
                                    }}
                                >
                                    HOME
                                </a>
                            </MenuItem>
                            {/* Dynamic menu items from database */}
                            {menuItems.map((menu) => {
                                const isExternalUrl = menu.menu_link.startsWith('http://') || menu.menu_link.startsWith('https://');
                                const href = isExternalUrl 
                                    ? menu.menu_link 
                                    : getUserFrontendUrl(menu.menu_link);
                                
                                return (
                                    <MenuItem key={menu.id} onClick={handleClose}>
                                        <a
                                            href={href}
                                            target={isExternalUrl ? "_blank" : undefined}
                                            rel={isExternalUrl ? "noopener noreferrer" : "noopener noreferrer"}
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                        >
                                            {menu.menu_title}
                                        </a>
                                    </MenuItem>
                                );
                            })}
                        </Menu>
                    </Toolbar>
                </AppBar>
            </Box>

            <Box sx={{ 
                maxWidth: 450, 
                mx: "auto", 
                textAlign: "center", 
                p: 2,
                minHeight: "100vh",
                position: "relative",
                zIndex: 1
            }}>
            <Typography variant="h6" fontWeight="bold" mt={1} sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 60, position: "relative" }}>
                {!logoLoaded && (
                    <Skeleton 
                        variant="rectangular" 
                        width={150} 
                        height={60} 
                        sx={{ borderRadius: 1, position: "absolute" }}
                    />
                )}
                <img 
                    src={logoUrl} 
                    width={150} 
                    alt="FMB logo" 
                    loading="eager"
                    fetchPriority="high"
                    style={{ 
                        opacity: logoLoaded ? 1 : 0,
                        transition: "opacity 0.3s ease-in-out",
                        position: "relative"
                    }}
                    onLoad={() => {
                        console.log('Logo loaded successfully');
                        setLogoLoaded(true);
                    }}
                    onError={(e) => {
                        console.error('Logo failed to load:', logoUrl, e);
                        // Show fallback logo if error
                        if (logoUrl !== FMBlogo) {
                            e.target.src = FMBlogo;
                        }
                        setLogoLoaded(true);
                    }}
                />
            </Typography>

            {showServingMessage && (
                <Alert 
                    severity="info" 
                    icon={false}
                    sx={{ 
                        mb: 2, 
                        p: 1.5,
                        backgroundColor: "#e3f2fd",
                        border: "2px solid #2196f3",
                        borderRadius: 2,
                        boxShadow: "0 2px 8px rgba(33, 150, 243, 0.2)",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center"
                    }}
                >
                    <Typography variant="body1" fontWeight="bold" sx={{ color: "#1976d2", textAlign: "center", width: "100%" }}>
                        Du blir betjent nå!
                    </Typography>
                </Alert>
            )}

            <Typography variant="body1" mt={0.5} fontWeight={"bold"}>
                Ditt kønummer er
            </Typography>
            <Typography sx={{ fontSize: 60 }} fontWeight="bold">
                {currentQueueNumber ?? (error ? "ERROR" : "...")}
            </Typography>
            {error && (
                <Typography variant="body2" color="error" mt={0.5} sx={{ fontSize: "0.8rem" }}>
                    {error}
                </Typography>
            )}
            {hasSelectedOptions ? (
                <Box sx={{ 
                    pb: 0,
                    mb: { xs: branchId ? 0 : 3, md: 2 } // Add bottom margin when no branchId on mobile
                }}>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mt: 1, mb: 0.5, fontSize: "0.875rem" }}
                    >
                        Dine valgte tjenester:
                    </Typography>
                    {displayServices.map((service, index) => (
                        <Card
                            key={index}
                            variant="outlined"
                            sx={{ 
                                mt: index === 0 ? 0 : 0.5,
                                mb: 0,
                                backgroundColor: "#f5f5f5"
                            }}
                        >
                            <CardContent sx={{ 
                                p: 1, 
                                pb: index === displayServices.length - 1 ? 0 : 1
                            }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
                                    {typeof service === "object"
                                        ? service.service_name
                                        : service}
                                </Typography>
                                <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                                    {typeof service === "object" && service.service_tag
                                        ? service.service_tag
                                        : "Spør oss om aktuelle kampanjer og tilbud."}
                                </Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            ) : !serviceSelectionDisabled && selectedOptions?.length === 0 && servicesFromApi.length === 0 && wasServiceSelectionEnabledAtCreation ? (
                // Only show default services if:
                // 1. Service selection is currently enabled
                // 2. AND there are no selectedOptions (user didn't select any)
                // 3. AND there are no services from API
                // 4. AND service selection was enabled when queue was created
                // This prevents showing default services when service selection was disabled when queue was created
                // and then enabled later
                <>
                    <Typography
                        variant="body2"
                        fontWeight="bold"
                        sx={{ mt: 1, mb: 0.5, fontSize: "0.875rem" }}
                    >
                        Våre tjenester:
                    </Typography>
                    <Card
                        variant="outlined"
                        sx={{ mt: 0.5, mb: 0.5, backgroundColor: "#f5f5f5" }}
                    >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
                                Mal Proff
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                                Spør oss om aktuelle kampanjer og tilbud.
                            </Typography>
                        </CardContent>
                    </Card>

                    <Card
                        variant="outlined"
                        sx={{ 
                            mt: 0.5, 
                            backgroundColor: "#f5f5f5",
                            mb: { xs: branchId ? 0 : 3, md: 2 } // Add bottom margin when no branchId on mobile
                        }}
                    >
                        <CardContent sx={{ p: 1, "&:last-child": { pb: 1 } }}>
                            <Typography variant="body2" fontWeight="bold" sx={{ fontSize: "0.875rem" }}>
                                Tapet / Gardiner / Solskjerming
                            </Typography>
                            <Typography variant="body2" sx={{ fontSize: "0.75rem", mt: 0.5 }}>
                                Spør oss gjerne om aktuelle kampanjer og tilbud,
                                samt at vi har en del varer på lager til god
                                priser.
                            </Typography>
                        </CardContent>
                    </Card>
                </>
            ) : null}

            {/* Carousel for ads - only show if branchId is available */}
            {branchId && (
                <Box
                    sx={{
                        mt: 3,
                        mb: 3,
                        width: "100%",
                        height: "auto",
                        minHeight: "400px",
                        maxHeight: "600px",
                        borderRadius: 4,
                        overflow: "hidden",
                        position: "relative",
                        zIndex: 1,
                    }}
                >
                    <Carousel 
                        imageType="portrait" 
                        branchId={branchId}
                    />
                </Box>
            )}

            {/* Footer text with proper spacing */}
            <Box sx={{ mt: 3, mb: 2 }}>
                <Typography
                    variant="body1"
                    color="primary"
                    fontWeight="bold"
                    sx={{ fontSize: "0.9rem" }}
                >
                    easyQue (c) by Digifront
                </Typography>
                <Typography
                    variant="body2"
                    color="primary"
                    component="a"
                    href="mailto:sales@digifront.biz"
                    sx={{ fontSize: "0.8rem" }}
                >
                    sales@digifront.biz
                </Typography>
            </Box>
            <ScrollButtons />
            </Box>
        </>
    );
};

export default GetQueue;